// Единая мобильная шапка: лого-полоса, строка «заголовок + кнопка меню» и
// выдвижной сайдбар с содержимым бургера и попапа аккаунта в одном месте.
//
// До этого компонента у планировщика и у /trips были две разные мобильные
// шапки (Toolbar рисовал свою, TripsPage — свою), и у каждой был свой набор
// мелких расхождений: то знак не по центру, то кнопка аккаунта устроена
// иначе. Здесь всё это стало одним компонентом ровно затем, чтобы расходиться
// было больше нечему — оба экрана вставляют один и тот же узел, различаясь
// только тем, что в него передают (заголовок и, в планировщике, раздел
// «Поездка»).
//
// На десктопе компонент — no-op (возвращает null): там у каждого экрана своя
// шапка, и решать, рисовать её вообще или нет, оставлено вызывающей стороне.
//
// Заголовка может не быть вовсе (гость в планировщике: TripNameField рисует
// null, пока нет открытой поездки — своей поездки у гостя нет никогда).
// Тогда компонент вместо двух строк рисует одну: знак и кнопку меню в ряд,
// а <h1> никуда не девается — просто становится визуально скрытым (см.
// useIconsFit ниже и комментарий у title).
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import { useT } from '../../i18n';
import { AccountButton, SignOutIcon, useAuth, useSignOutAction } from '../../auth';
import { Logo } from './Logo';
import { useIsMobile } from './useIsMobile';
import { useDismissablePanel } from './useDismissablePanel';
import styles from './MobileShellHeader.module.css';

function BurgerIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function UserIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </svg>
  );
}

/**
 * Ширину «нужно ли для слова, ряда иконок и кнопки меню места в одну строку»
 * меряем настоящим DOM-замером (ResizeObserver + невидимый двойник), а не
 * захардкоженным брейкпоинтом и не container query по ширине самой строки.
 *
 * Брейкпоинт по px врёт: подпись кнопки меню — это account.title, а он на
 * пяти языках разной длины («Аккаунт» короче «Λογαριασμός» в полтора раза),
 * и число, при котором иконки перестают влезать, у каждого языка своё.
 * Container query по ширине контейнера страдает тем же: строка сама всегда
 * шириной с экран (растягивается как блочный flex-контейнер), её @container
 * увидел бы только ширину вьюпорта, а нужна разница между «сколько есть» и
 * «сколько просят» слово+иконки+кнопка вместе — а это и есть переполнение,
 * которое можно узнать только измерив настоящий контент.
 *
 * Меряем не саму видимую строку (в ней иконки то есть, то спрятаны — с ними
 * scrollWidth после первого же скрытия перестал бы отражать «поместились бы
 * иконки, если бы место появилось» и обратный показ был бы не гарантирован),
 * а невидимый двойник, в котором иконки показаны всегда. Логотип для двойника
 * безопасно рендерить второй раз — в отличие от TripNameField у него нет ни
 * состояния, ни эффектов, а видимая кнопка меню и так уже настоящая, дублировать
 * её незачем: меряем её реальный элемент через menuBtnRef.
 */
function useIconsFit(deps: readonly unknown[]): {
  rowRef: RefObject<HTMLDivElement | null>;
  probeRef: RefObject<HTMLDivElement | null>;
  menuBtnRef: RefObject<HTMLButtonElement | null>;
  iconsFit: boolean;
} {
  const rowRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const [iconsFit, setIconsFit] = useState(true);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const probe = probeRef.current;
    const btn = menuBtnRef.current;
    if (!row || !probe || !btn) return;

    function check(): void {
      if (!row || !probe || !btn) return;
      // probe — position: absolute с шириной по содержимому (см. .probe),
      // поэтому его собственная ширина — это ровно «сколько нужно знаку с
      // иконками», без хаков с временным показом/скрытием чего-либо. Кнопку
      // не дублируем — меряем настоящую (menuBtnRef) и добавляем зазор между
      // ней и знаком тем же значением, что задаёт gap строки в CSS: держать
      // то же число ещё и здесь, как константу, означало бы рано или поздно
      // развести его с версткой.
      const rowStyle = getComputedStyle(row);
      const paddingX =
        parseFloat(rowStyle.paddingLeft || '0') + parseFloat(rowStyle.paddingRight || '0');
      const gap = parseFloat(rowStyle.columnGap || rowStyle.gap || '0') || 0;
      const available = row.clientWidth - paddingX;
      const needed = probe.getBoundingClientRect().width + gap + btn.getBoundingClientRect().width;
      setIconsFit(needed <= available);
    }

    check();
    // Реагируем на реальные изменения размеров, а не только на смену пропов:
    // поворот экрана, ресайз окна и смена кегля в персонализации меняют
    // доступную ширину или ширину знака без обязательного ре-рендера этого
    // компонента. Оба наблюдения безопасны — .disconnect() в cleanup.
    const ro = new ResizeObserver(check);
    ro.observe(row);
    ro.observe(probe);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { rowRef, probeRef, menuBtnRef, iconsFit };
}

export interface MobileShellHeaderProps {
  /**
   * Заголовок второй строки шапки: на /trips — строка t('trips.title'), в
   * планировщике — <TripNameField/>, тот же узел, что раньше приходил в
   * Toolbar пропом tripName. Экран отдаёт ровно один <h1> в любом случае —
   * либо этот (видимый), либо, когда его нет, визуально скрытый запасной
   * (см. компонент).
   *
   * Бывает пустым: у гостя в планировщике открытой поездки нет никогда, и
   * вызывающий экран (App.tsx) передаёт вместо узла честное отсутствие —
   * тогда вместо двух строк рисуется одна.
   */
  title?: ReactNode;
  /**
   * Раздел «Поездка» сайдбара: название (оно теперь заголовок, а не строка
   * здесь), тур, путешественники, валюта, курсы, снимки — набор строк
   * AppMenuRow/AppMenuBlock, собранный вызывающим экраном. На /trips его нет
   * вовсе — там сайдбар состоит из одной секции «Аккаунт».
   */
  tripSection?: ReactNode;
  /**
   * Открыть форму входа сразу, как только смонтируется встроенная кнопка
   * аккаунта — например, по ссылке из письма о сбросе пароля. Кнопка живёт
   * внутри сайдбара и монтируется вместе с ним, поэтому эффект сработает
   * только когда сайдбар открыт; это та же особенность, что была у старого
   * бургер-меню, а не новый недостаток этого компонента.
   */
  accountInitialSignIn?: boolean;
}

export function MobileShellHeader({
  title,
  tripSection,
  accountInitialSignIn,
}: MobileShellHeaderProps): ReactElement | null {
  const isMobile = useIsMobile();
  const t = useT();
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Esc и фокус-менеджмент — та же механика, что у бургера (см. AppMenu.tsx),
  // вынесенная в общий хук, чтобы у двух панелей поведение не разъезжалось.
  const sheetRef = useDismissablePanel(open, close);

  const accountName = auth.status === 'user' ? auth.user?.name || auth.user?.email : undefined;
  const menuLabel = accountName || t('account.title');

  // Замер нужен только у однострочного варианта (см. return ниже), но хуки
  // не могут вызываться условно — вызывается всегда, а его рефы просто
  // остаются ни к чему не привязанными в двухстрочном варианте (внутренняя
  // проверка в эффекте на этот случай и рассчитана). title и menuLabel — в
  // зависимостях: первое переключает саму раскладку (см. return), второе
  // меняет ширину подписи кнопки при смене языка или входе в аккаунт.
  const { rowRef, probeRef, menuBtnRef, iconsFit } = useIconsFit([title, menuLabel]);

  // Гостю и во время загрузки статуса знак ведёт на корень — список поездок
  // недоступен, пока не вошёл (см. тот же выбор в Toolbar для десктопа).
  const logoHref = auth.status === 'user' ? '/trips' : '/';

  // Десктоп рисует свою шапку сам — этот компонент для него не существует.
  if (!isMobile) return null;

  // Кнопка меню общая для обеих раскладок (см. return): в двухстрочной она
  // стоит в titleRow рядом с <h1>, в однострочной — в одном ряду со знаком.
  // Ref собирает сразу два адресата: triggerRef нужен close() для возврата
  // фокуса, menuBtnRef — единственному замеру ширины в useIconsFit.
  const menuButton = (
    <button
      ref={(el) => {
        triggerRef.current = el;
        menuBtnRef.current = el;
      }}
      type="button"
      className={styles.menuBtn}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={t('app.menu')}
      onClick={() => setOpen(true)}
    >
      <BurgerIcon />
      <UserIcon />
      <span className={styles.menuBtnLabel}>{menuLabel}</span>
    </button>
  );

  return (
    <header className={styles.header}>
      {title ? (
        <>
          {/* A1: самая верхняя строка экрана, знак во всю ширину — layout="bar"
              раскладывает слово и иконки в одну строку вместо вертикальной
              стопки десктопного/попапного знака. */}
          <div className={styles.logoBar}>
            <Logo layout="bar" title={t('app.brand')} href={logoHref} />
          </div>

          <div className={styles.titleRow}>
            <h1 className={styles.title}>{title}</h1>
            {menuButton}
          </div>
        </>
      ) : (
        // Заголовка нет (гость в планировщике — своей поездки у него нет
        // никогда, см. проп title): вместо двух строк (полноразмерная
        // лого-полоса сверху, <h1> с кнопкой снизу) — одна строка со знаком и
        // кнопкой меню, центрированная и без переносов. Ряд иконок знака
        // прячется целиком, если в строке для него не остаётся места (см.
        // useIconsFit) — обрезанная наполовину иконка читалась бы как
        // поломка, а не как разумный компромисс.
        <div ref={rowRef} className={styles.singleLine}>
          {/* <h1> остаётся ровно один на экран и здесь — просто визуально
              скрытый (класс visually-hidden из global.css, как <caption> в
              ComparePanel.tsx): экрану есть что назвать (само приложение),
              даже когда конкретной поездки ещё нет, а страница без единого
              заголовка первого уровня — дыра для скринридера, не экономия. */}
          <h1 className="visually-hidden">{t('app.brand')}</h1>
          <Logo layout="inline" title={t('app.brand')} href={logoHref} showIcons={iconsFit} />
          {menuButton}
          {/* Невидимый двойник знака — только для замера (см. useIconsFit):
              его ширина при постоянно включённых иконках и есть то самое
              «сколько нужно места», с которым сверяется настоящий iconsFit.
              position: absolute в .probe убирает его из потока — он ничего
              не сдвигает и не читается скринридером (aria-hidden). */}
          <div ref={probeRef} className={styles.probe} aria-hidden="true">
            <Logo layout="inline" title={t('app.brand')} href={logoHref} />
          </div>
        </div>
      )}

      {open && (
        <div
          className={styles.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div
            ref={sheetRef}
            className={styles.sheet}
            role="dialog"
            aria-modal="true"
            aria-label={t('app.menu')}
            tabIndex={-1}
          >
            <div className={styles.head}>
              <span className={styles.headTitle}>{t('app.menu')}</span>
              <button
                type="button"
                className={styles.closeBtn}
                aria-label={t('form.close')}
                onClick={close}
              >
                ✕
              </button>
            </div>

            {/* Тот же приём, что у бургера: элементы, уводящие с панели
                (переход на другой экран, окно поверх), помечены атрибутом
                data-menu-close самим содержимым (AccountMenu.tsx). Контролы,
                которые крутят настройку на месте — валюта, курсы, кегль, —
                этим атрибутом не помечены и оставляют сайдбар открытым. */}
            <div
              className={styles.body}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('[data-menu-close]')) close();
              }}
            >
              {tripSection && (
                <section className={styles.section}>
                  <h3 className={styles.legend}>{t('menu.section.trip')}</h3>
                  <div className={styles.rows}>{tripSection}</div>
                </section>
              )}

              {/* Аккаунтную секцию компонент собирает сам через тот же
                  AccountButton(inline), которым раньше пользовался бургер:
                  один и тот же путь для попапа, старого бургера и нового
                  сайдбара — значит, один и тот же набор модалок настроек и
                  персонализации без лишнего кода здесь. hideSignOut прячет
                  «Выйти» из списка — оно переехало в футер ниже. */}
              <section className={styles.section}>
                <h3 className={styles.legend}>{t('menu.section.account')}</h3>
                <div className={styles.rows}>
                  <AccountButton inline hideSignOut initialSignIn={accountInitialSignIn} />
                </div>
              </section>
            </div>

            {/* B5: «Выйти» — отдельный футер, а не последняя строка списка.
                margin-top: auto в CSS прижимает его к низу панели независимо
                от того, сколько строк выше — те скроллятся своим .body, а
                футер остаётся на месте. Показывается только вошедшему: гостю
                соответствующим действием служит «Войти» внутри секции
                аккаунта. */}
            {auth.status === 'user' && <SignOutFooter onClose={close} />}
          </div>
        </div>
      )}
    </header>
  );
}

/**
 * Вынесен отдельным компонентом, а не инлайном в MobileShellHeader: он
 * рисуется только вошедшему (см. условие в месте вызова), и держать его как
 * отдельный компонент, а не звать useSignOutAction прямо в MobileShellHeader,
 * значит не подписываться на useRoute гостю, которому кнопка выхода всё
 * равно не нужна.
 */
function SignOutFooter({ onClose }: { onClose: () => void }): ReactElement {
  const t = useT();
  const doSignOut = useSignOutAction(onClose);
  return (
    <div className={styles.footer}>
      <button type="button" className={styles.signOutBtn} onClick={doSignOut}>
        <SignOutIcon />
        <span>{t('auth.signout')}</span>
      </button>
    </div>
  );
}
